import { useTranslateControlWithParameters } from "@/features/controls/hooks/use-translate-control-with-parameters";
import {
  COLLAPSED_SIDEBAR_WIDTH,
  FULL_SIDEBAR_WIDTH,
} from "@/shared/components/app-sidebar";
import { useQueryParams } from "@/shared/hooks";
import { useSheetStore, useSidebarStore } from "@/shared/stores";
import { useSelectedControlStore } from "@/shared/stores/selected-control.store";
import { useCallback, useEffect, useMemo } from "react";
import { z } from "zod";
import { ControlResultDetails } from "../components/control-result-details";
import { ResultDetailSheetHeader } from "../components/details-sheet/result-detail-sheet-header";

const FULLSCREEN_QUERY_PARAM = "fullscreen";

export const useControlResultDetailsSheet = ({
  controlId,
  resultGroupId,
  companyId,
}: {
  controlId: string;
  resultGroupId: string;
  companyId: string;
}) => {
  const { checkQueryParam, setQueryParam, deleteQueryParam } = useQueryParams();
  const { isExpanded } = useSidebarStore();
  const { openSheet, closeSheet, updateWidth, toggleFullscreen } =
    useSheetStore();

  const titleResultGroupId = useMemo(() => {
    return z.string().uuid().safeParse(resultGroupId).success
      ? resultGroupId.slice(-8)
      : resultGroupId;
  }, [resultGroupId]);

  const getIsFullscreen = useCallback(
    () => checkQueryParam(FULLSCREEN_QUERY_PARAM),
    [checkQueryParam],
  );

  const getWidth = useCallback(() => {
    const sidebarWidth = isExpanded
      ? FULL_SIDEBAR_WIDTH
      : COLLAPSED_SIDEBAR_WIDTH;
    const baseWidth = getIsFullscreen() ? "100vw" : "75vw";
    return `calc(${baseWidth} - ${sidebarWidth}px)`;
  }, [isExpanded, getIsFullscreen]);

  const width = getWidth();
  const isFullscreen = getIsFullscreen();

  useEffect(() => {
    updateWidth(width);
  }, [updateWidth, width]);

  useEffect(() => {
    toggleFullscreen(isFullscreen);
  }, [toggleFullscreen, isFullscreen]);

  const cleanSheetQueryParams = useCallback(() => {
    deleteQueryParam("r");
    deleteQueryParam(FULLSCREEN_QUERY_PARAM);
  }, [deleteQueryParam]);

  const handleToggleFullscreen = useCallback(() => {
    const isFullscreen = getIsFullscreen();

    if (isFullscreen) {
      deleteQueryParam(FULLSCREEN_QUERY_PARAM);
    } else {
      setQueryParam(FULLSCREEN_QUERY_PARAM, "true");
    }
  }, [getIsFullscreen, deleteQueryParam, setQueryParam]);

  const { control } = useSelectedControlStore();

  const descriptorName = useTranslateControlWithParameters(
    {
      name: control?.descriptorLabelKeyName ?? "unknown_control",
      currency: control?.currency ?? "",
    },
    control?.parameters ?? [],
  );

  const onLoadError = useCallback(() => {
    cleanSheetQueryParams();
    closeSheet();
  }, [cleanSheetQueryParams, closeSheet]);

  return {
    open: useCallback(
      (resultId: string) => {
        openSheet({
          header: (
            <ResultDetailSheetHeader
              controlId={controlId}
              titleControlResultId={titleResultGroupId}
              descriptorName={descriptorName}
              companyId={companyId}
              resultId={resultId}
              onLoadError={onLoadError}
            />
          ),
          content: (
            <ControlResultDetails
              controlId={controlId}
              controlResultId={resultId}
              companyId={companyId}
              onLoadError={onLoadError}
            />
          ),
          onCloseCallback: () => {
            cleanSheetQueryParams();
          },
          onToggleFullscreen: () => {
            handleToggleFullscreen();
          },
          enableFullscreen: true,
          enableCopyUrl: true,
          width,
          isFullscreen: getIsFullscreen(),
        });
      },
      [
        titleResultGroupId,
        controlId,
        companyId,
        width,
        descriptorName,
        openSheet,
        cleanSheetQueryParams,
        getIsFullscreen,
        handleToggleFullscreen,
        onLoadError,
      ],
    ),
    clean: () => {
      cleanSheetQueryParams();
    },
    autoOpen: checkQueryParam("r"),
  };
};
